/**
 * Shared user-facing date & time formatting utilities.
 * Use these for any text shown to users, so we have a single place
 * to tweak formats (e.g. 12-hour vs 24-hour, locale, etc.).
 */

import { differenceInSeconds, differenceInMinutes, differenceInHours, differenceInDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

export type TimeFormatOptions = {
  /**
   * When true, shows minutes even if they are 0.
   * e.g. 6:00pm instead of 6pm.
   * Defaults to false.
   */
  alwaysShowMinutes?: boolean;
  /**
   * When true, shows the time range as "All day" instead of the time range.
   * Defaults to false.
   */
  showNumericMidnight?: boolean;
};

/**
 * Formats an hour/minute pair into a 12‑hour clock string with am/pm.
 *
 * Examples:
 * - 18:00 -> "6pm"
 * - 18:30 -> "6:30pm"
 * - 0:00  -> "12am"
 */
export function formatTime12(hour: number, minute: number, options: TimeFormatOptions = {}): string {
  const { alwaysShowMinutes = false, showNumericMidnight = false } = options;

  // Normalize to 0–23 just in case
  const normalizedHour = ((hour % 24) + 24) % 24;

  // Special-case midnight to read as a label instead of "12am"
  if (normalizedHour === 0 && minute === 0) {
    if (showNumericMidnight) {
      return "12am";
    }
    return "Midnight";
  }

  const isPm = normalizedHour >= 12;
  const displayHour =
    normalizedHour === 0 ? 12
    : normalizedHour > 12 ? normalizedHour - 12
    : normalizedHour;

  const amPm = isPm ? "pm" : "am";

  if (!alwaysShowMinutes && minute === 0) {
    return `${displayHour}${amPm}`;
  }

  const minutePart = minute.toString().padStart(2, "0");
  return `${displayHour}:${minutePart}${amPm}`;
}

/**
 * Formats a date as "Thursday 25th December"
 */
export function formatDateVerbose(dateString: string): string {
  if (!dateString) return "";
  // Extract just the date portion to avoid timezone shifts
  const datePart = dateString.split("T")[0];
  if (!datePart) return "";
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return "";
  // Create date in local timezone using the correct year/month/day
  const date = new Date(year, month - 1, day);

  const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
  const monthName = date.toLocaleDateString("en-US", { month: "long" });
  const dayOfMonth = date.getDate();

  // Get ordinal suffix
  const getOrdinalSuffix = (n: number): string => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${dayName} ${dayOfMonth}${getOrdinalSuffix(dayOfMonth)} ${monthName}`;
}

/**
 * Formats an ISO instant as an absolute date + time, e.g.
 * "31 Jul 2026, 11:33 AM".
 *
 * `timeZone` is an optional IANA zone (e.g. "Australia/Sydney"). When given, the
 * instant is rendered in that zone — used for per-site reports (RoadSense) where
 * the reader expects site-local wall-clock, matching the rest of that report.
 * When omitted, it renders in the viewer's local timezone.
 *
 * Returns "" for an empty/invalid input so callers can conditionally omit the
 * line rather than render "Invalid Date".
 */
export function formatDateTime(isoInstant: string | undefined, timeZone?: string): string {
  if (!isoInstant) return "";
  const date = new Date(isoInstant);
  if (Number.isNaN(date.getTime())) return "";
  const pattern = "d MMM yyyy, h:mm a";
  // A viewer-local render uses `date-fns`' zone-aware formatter pinned to the
  // resolved local zone, so both branches go through one code path (no separate
  // `format` import) and behave identically bar the target zone.
  return formatInTimeZone(date, timeZone ?? resolvedLocalTimeZone(), pattern);
}

/**
 * Formats an ISO instant as a `DD-MM-YYYY` date + time, e.g.
 * "31-07-2026, 11:59 AM". Same behaviour and timezone handling as
 * `formatDateTime`, but the date part is `DD-MM-YYYY` so it matches the
 * WasteSense reporting-window label (which the generator emits as
 * `01-06-2026 to 07-06-2026`). Kept separate from `formatDateTime` so the
 * existing "31 Jul 2026" verbose style used elsewhere (e.g. RoadSense) is
 * untouched.
 */
export function formatDateTimeDmy(isoInstant: string | undefined, timeZone?: string): string {
  if (!isoInstant) return "";
  const date = new Date(isoInstant);
  if (Number.isNaN(date.getTime())) return "";
  const pattern = "dd-MM-yyyy, h:mm a";
  return formatInTimeZone(date, timeZone ?? resolvedLocalTimeZone(), pattern);
}

/**
 * The viewer's IANA timezone (e.g. "Australia/Sydney"), read from the runtime's
 * Intl settings. Falls back to UTC on the rare host that doesn't resolve one.
 */
function resolvedLocalTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/**
 * Formats a date as a short relative time (e.g. "now", "5s ago", "2m ago", "3h ago", "2d ago").
 * Use for snapshot timestamps, activity "time ago", etc.
 */
export function formatTimeAgo(date: Date): string {
  const now = new Date();
  const secs = Math.max(0, differenceInSeconds(now, date));
  if (secs < 60) return `${secs}s ago`;
  const mins = differenceInMinutes(now, date);
  if (mins < 60) return `${mins}m ago`;
  const hours = differenceInHours(now, date);
  if (hours < 24) return `${hours}h ago`;
  const days = differenceInDays(now, date);
  return `${days}d ago`;
}
